import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiZap, FiDatabase, FiLayers, FiCheckCircle, FiAlertCircle, FiLoader, FiPlay, FiTarget, FiCalendar } from 'react-icons/fi';
import OrbitCard from '../components/OrbitCard';
import { upcomingAssignments, projectMilestones } from '../data/mockData';

const Demo = () => {
  const [activeTab, setActiveTab] = useState('journey');
  const [demoStep, setDemoStep] = useState(0);

  const workflowSteps = [
    { title: "Initialize Goals", desc: "Define your grade targets and exam dates.", icon: FiTarget },
    { title: "Sync Syllabus", desc: "Import course modules and assignment schedules.", icon: FiCalendar },
    { title: "Orbit Mapping", desc: "Visualize deadlines in the halo-ring dashboard.", icon: FiZap },
    { title: "Deep Work Session", desc: "Engage the focus timer to isolate distractions.", icon: FiPlay },
    { title: "Knowledge Retention", desc: "Review AI-generated flashcards for key concepts.", icon: FiLayers },
    { title: "Metric Analysis", desc: "Analyze grade trajectory and milestone completion.", icon: FiCheckCircle },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-12">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-slate-400 text-sm font-medium mb-1 uppercase tracking-wider">Product Showcase</h2>
          <h1 className="text-3xl font-bold">Demo / <span className="text-[#eaff00]">How It Works</span></h1>
        </div>
        <div className="flex bg-[#15172b] p-1 rounded-xl border border-[#2a2d4f] w-full md:w-auto">
          {['journey', 'data', 'states'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 md:px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab ? 'bg-[#eaff00] text-[#0b0c1b]' : 'text-slate-400 hover:text-white'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1).replace('journey', 'User Journey').replace('data', 'Sample Data').replace('states', 'Simulated States')}
            </button>
          ))}
        </div>
      </header>

      <AnimatePresence mode="wait">
        {activeTab === 'journey' && (
          <motion.div 
            key="journey"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-8"
          >
            <section className="glass-panel p-8 rounded-[2.5rem] relative overflow-hidden">
               <div className="absolute top-0 right-0 w-64 h-64 bg-[#eaff00]/5 rounded-full blur-3xl -mr-32 -mt-32"></div>
               <h3 className="text-xl font-bold mb-6 flex items-center">
                 <SafeIcon icon={FiZap} className="mr-3 text-[#eaff00]" />
                 Try This Flow: The StudyHalo Cycle
               </h3>
               
               <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                 <div className="space-y-4">
                   {workflowSteps.map((step, idx) => (
                     <div 
                       key={idx}
                       onClick={() => setDemoStep(idx)}
                       className={`p-4 rounded-2xl cursor-pointer transition-all border ${
                         demoStep === idx 
                          ? 'bg-[#eaff00]/10 border-[#eaff00]/40 translate-x-2' 
                          : 'bg-[#0b0c1b]/50 border-transparent hover:border-[#2a2d4f]'
                       }`}
                     >
                       <div className="flex items-center">
                         <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-4 ${demoStep === idx ? 'bg-[#eaff00] text-[#0b0c1b]' : 'bg-[#2a2d4f] text-slate-400'}`}>
                           <SafeIcon icon={step.icon} />
                         </div>
                         <div>
                            <h4 className={`font-semibold text-sm ${demoStep === idx ? 'text-[#eaff00]' : 'text-slate-200'}`}>{step.title}</h4>
                            <p className="text-xs text-slate-500 mt-0.5">{step.desc}</p>
                         </div>
                       </div>
                     </div>
                   ))}
                 </div>

                 <div className="flex flex-col justify-center items-center text-center p-8 bg-[#0b0c1b]/60 rounded-[2rem] border border-[#2a2d4f]">
                    <motion.div
                      key={demoStep}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="space-y-6"
                    >
                      <div className="w-24 h-24 rounded-full bg-[#15172b] border-2 border-[#eaff00]/30 flex items-center justify-center mx-auto glow-yellow">
                        <SafeIcon icon={workflowSteps[demoStep].icon} className="text-4xl text-[#eaff00]" />
                      </div>
                      <h4 className="text-2xl font-bold">{workflowSteps[demoStep].title}</h4>
                      <p className="text-slate-400 max-w-xs mx-auto">
                        This module interacts with the central database to ensure your {workflowSteps[demoStep].title.toLowerCase()} are synchronized across all devices.
                      </p>
                      <button className="bg-[#eaff00] text-[#0b0c1b] px-8 py-3 rounded-xl font-bold hover:scale-105 transition-transform">
                        Execute Module
                      </button>
                    </motion.div>
                 </div>
               </div>
            </section>
          </motion.div>
        )}

        {activeTab === 'data' && (
          <motion.div 
            key="data"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            <div className="glass-panel p-6 rounded-3xl">
              <h3 className="text-lg font-bold mb-4 flex items-center"><SafeIcon icon={FiDatabase} className="mr-2 text-[#eaff00]" /> Project Milestones</h3>
              <div className="space-y-4">
                {projectMilestones.map(m => (
                  <div key={m.id} className="flex items-center justify-between p-3 bg-[#0b0c1b]/40 rounded-xl border border-[#2a2d4f]">
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-3 ${m.status === 'completed' ? 'bg-[#eaff00]' : m.status === 'in-progress' ? 'bg-blue-400 animate-pulse' : 'bg-slate-700'}`}></div>
                      <span className="text-sm font-medium">{m.title}</span>
                    </div>
                    <span className="text-xs text-slate-500">{m.date}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-panel p-6 rounded-3xl">
              <h3 className="text-lg font-bold mb-4 flex items-center"><SafeIcon icon={FiLayers} className="mr-2 text-[#eaff00]" /> Assignment Orbit</h3>
              <div className="flex flex-col gap-4">
                {upcomingAssignments.slice(0, 3).map(item => (
                   <div key={item.id} className="scale-90 origin-left">
                     <OrbitCard item={item} />
                   </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'states' && (
          <motion.div 
            key="states"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            <div className="space-y-6">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">In Progress / Loading</p>
              <div className="glass-panel p-6 rounded-2xl border-blue-500/20">
                <div className="flex items-center justify-center h-24">
                  <div className="flex flex-col items-center">
                    <SafeIcon icon={FiLoader} className="text-3xl text-blue-400 animate-spin mb-3" />
                    <span className="text-xs text-blue-400 font-medium">Syncing Neural Nodes...</span>
                  </div>
                </div>
              </div>

              <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Success / Complete</p>
              <div className="glass-panel p-6 rounded-2xl border-[#eaff00]/20">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-[#eaff00]/10 flex items-center justify-center mb-3">
                    <SafeIcon icon={FiCheckCircle} className="text-2xl text-[#eaff00]" />
                  </div>
                  <h4 className="font-bold text-[#eaff00]">Session Optimized</h4>
                  <p className="text-[10px] text-slate-500 mt-1">94% efficiency reached in Deep Work mode.</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Empty / Idle</p>
              <div className="glass-panel p-6 rounded-2xl border-slate-700">
                <div className="flex flex-col items-center text-center justify-center h-48 py-8">
                  <div className="w-16 h-16 rounded-full bg-slate-800/50 flex items-center justify-center mb-4 text-slate-600">
                    <SafeIcon icon={FiLayers} className="text-2xl" />
                  </div>
                  <p className="text-sm text-slate-500 font-medium italic">"The orbit is clear. Add your first mission to begin."</p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Warning / Critical</p>
              <div className="glass-panel p-6 rounded-2xl border-red-500/20">
                <div className="flex items-start">
                  <div className="p-2 bg-red-500/10 rounded-lg mr-4">
                    <SafeIcon icon={FiAlertCircle} className="text-xl text-red-500" />
                  </div>
                  <div>
                    <h4 className="font-bold text-red-400 text-sm">System Conflict</h4>
                    <p className="text-[10px] text-slate-500 mt-1">Found 2 exams overlapping on Nov 14. Resolve in Calendar.</p>
                    <button className="mt-3 text-[10px] bg-red-500/20 text-red-400 px-3 py-1 rounded-md border border-red-500/20">Resolve Nodes</button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <footer className="pt-12 text-center opacity-40">
        <p className="text-[10px] text-slate-500 uppercase tracking-widest">Product Integrity Protocol v2.5</p>
        <p className="text-xs text-slate-400 mt-1 italic">Made by LingLink AI and DigLab AI</p>
      </footer>
    </div>
  );
};

export default Demo;